// MODAL ARCHIVOS
import React, { useState, useEffect, useRef, forwardRef } from "react";
import {
  FormControlLabel,
  Button,
  Checkbox,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Slide,
  Autocomplete,
  TextField,
} from "@mui/material/";

// ICONS
import { Close, CloudUpload, FileUpload } from "@mui/icons-material";
import api from "../../../../api/axios";
import { normalizeFolderDisplayName } from "../docsFolderDisplay";

const UploaderDialog = ({
  setSelectedFolderTypeOption,
  selectedFolderTypeOption,
  caseId,
  setDocsChangedFlag,
  setDocsLoading,
  docsChangedFlag,
  effects,
  userLogged,
  dataCase,
  waitForDocsRender,
}) => {
  const Swal = require("sweetalert2");

  // Estados principales del componente
  const [files, setFiles] = useState([]); // Lista de archivos a subir

  // Referencias a inputs de archivos y carpetas
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const [selectedDocTypeOption, setSelectedDocTypeOption] = useState(null); // Tipo de documento seleccionado
  const [otherNameFolder, setOtherNameFolder] = useState(""); // Nombre de carpeta personalizada
  const [uploadDocWithFileName, setUploadDocWithFileName] = useState(false); // Indica si los documentos ya están listos para mostrar
  const [openUploader, setOpenUploader] = useState(false);

  // Opciones para tipo de documento
  const docType = [
    { name: "Seleccionar", value: 0 },
    { name: "Subir Carpeta", value: 1 },
    { name: "Subir Archivo", value: 2 },
  ];

  // Autocomplete para seleccionar tipo de documento
  const DocTypeAutocomplete = ({ label }) => (
    <Grid item xs={12}>
      <Autocomplete
        sx={{ width: "100%" }}
        size="small"
        value={selectedDocTypeOption}
        onChange={handleSelection}
        options={docType}
        getOptionLabel={(option) => (option?.name ? option.name : "")}
        renderInput={(params) => (
          <TextField {...params} label={label} variant="outlined" />
        )}
      />
    </Grid>
  );

  const isStandardFolder = (value) => [1, 2, 5, 6, 7].includes(Number(value));

  // Mapea el tipo de carpeta seleccionada con el campo o componente que se debe renderizar
  const folderTypeFields = {
    1: (
      <DocTypeAutocomplete
        label={`Guardar en ${
          selectedFolderTypeOption?.displayName ||
          normalizeFolderDisplayName(selectedFolderTypeOption?.name) ||
          ""
        }`}
      />
    ),
    2: (
      <DocTypeAutocomplete
        label={`Guardar en ${
          selectedFolderTypeOption?.displayName ||
          normalizeFolderDisplayName(selectedFolderTypeOption?.name) ||
          ""
        }`}
      />
    ),
    5: (
      <DocTypeAutocomplete
        label={`Guardar en ${
          selectedFolderTypeOption?.displayName ||
          normalizeFolderDisplayName(selectedFolderTypeOption?.name) ||
          ""
        }`}
      />
    ),
    6: (
      <DocTypeAutocomplete
        label={`Guardar en ${
          selectedFolderTypeOption?.displayName ||
          normalizeFolderDisplayName(selectedFolderTypeOption?.name) ||
          ""
        }`}
      />
    ),
    7: (
      <DocTypeAutocomplete
        label={`Guardar en ${
          selectedFolderTypeOption?.displayName ||
          normalizeFolderDisplayName(selectedFolderTypeOption?.name) ||
          ""
        }`}
      />
    ),
    3: <DocTypeAutocomplete label="Guardar en Sin Carpeta" />,
    4: (
      <Grid container item xs={12} p={2}>
        {/* Campo para introducir el nombre personalizado de la carpeta */}
        <Grid item xs={12} sx={{ margin: "0px 0px 20px 0px" }}>
          <TextField
            label="Nombre de la carpeta"
            variant="outlined"
            size="small"
            onChange={(e) => setOtherNameFolder(e.target.value)}
            value={otherNameFolder}
            fullWidth
          />
        </Grid>
        {/* Selección del tipo de documento para el nuevo folder */}
        <DocTypeAutocomplete label={`Guardar en una carpeta nueva`} />
      </Grid>
    ),
  };

  // Cierra modal y reinicia opciones
  const handleCloseModal = () => {
    //   Quita el foco de cualquier input activo
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    setOpenUploader(false);
    setSelectedFolderTypeOption(null);
    setSelectedDocTypeOption(null);
    setFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = null;
    if (folderInputRef.current) folderInputRef.current.value = null;
  };

  // Sube archivos al servidor
  const handleUpload = async () => {
    handleCloseModal();

    if (files.length === 0) {
      return Swal.fire({
        title: "¡Error!",
        icon: "error",
        text: "No hay archivos seleccionados",
        confirmButtonColor: "#1ab394",
        confirmButtonText: "Continuar",
      });
    }

    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files[]", file);
    });
    formData.append("case_id", caseId);

    // Determina carpeta destino
    if (isStandardFolder(selectedFolderTypeOption.value)) {
      formData.append("folder", selectedFolderTypeOption.name || "");
    } else if (selectedFolderTypeOption.value === 3) {
      formData.append("folder", "");
    } else if (selectedFolderTypeOption.value === 4) {
      formData.append("folder", otherNameFolder || "");
    }

    formData.append("user_id", userLogged?.id);

    if (uploadDocWithFileName) {
      formData.append("flag_name", uploadDocWithFileName);
    }

    Swal.fire({
      title: "Actualizando archivos...",
      html: `
        <div>Procesando y refrescando documentos</div>
        <div style="margin-top:10px;padding:10px 12px;border-radius:10px;background:#eef6ff;color:#0b4a8b;font-weight:700;font-size:13px;line-height:1.35;">
          Espere unos segundos mientras se renderizan los archivos en pantalla.
        </div>
      `,
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const uploadResponse = await api.post(
        `${process.env.REACT_APP_API_URL}/s3/store`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      const dbFailed = Number(uploadResponse?.data?.db_failed || 0);

      const waitForRenderWithTimeout = async (timeoutMs = 20000) => {
        if (typeof waitForDocsRender !== "function") {
          return;
        }

        await Promise.race([
          waitForDocsRender(),
          new Promise((_, reject) => {
            setTimeout(
              () => reject(new Error("Timeout esperando render de documentos")),
              timeoutMs,
            );
          }),
        ]);
      };

      if (typeof setDocsLoading === "function") {
        setDocsLoading(true);
      }

      setDocsChangedFlag((prev) => !prev);

      let renderError = null;
      try {
        await waitForRenderWithTimeout();
      } catch (renderErr) {
        renderError = renderErr;
        console.error(renderErr);
      }

      Swal.close();

      if (renderError) {
        if (typeof setDocsLoading === "function") {
          setDocsLoading(false);
        }

        Swal.fire({
          title: "Subida completada con advertencia",
          icon: "warning",
          text: "Los archivos se enviaron, pero falló la recarga de documentos en pantalla. Revisa la conexión con S3 o recarga la vista.",
          confirmButtonColor: "#1ab394",
          confirmButtonText: "Continuar",
        });
        return;
      }

      if (dbFailed > 0) {
        Swal.fire({
          title: "Subida parcial en base de datos",
          icon: "warning",
          text: `Se subieron los archivos a S3, pero ${dbFailed} nombre(s) no se pudieron registrar en la base de datos.`,
          confirmButtonColor: "#1ab394",
          confirmButtonText: "Continuar",
        });
        return;
      }

      Swal.fire({
        title: "¡Guardado!",
        icon: "success",
        text: "Los documentos se actualizaron correctamente en pantalla.",
        confirmButtonColor: "#1ab394",
        confirmButtonText: "Continuar",
      });
    } catch (err) {
      if (typeof setDocsLoading === "function") {
        setDocsLoading(false);
      }
      Swal.close();
      console.error(err);

      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "No se pudo subir la documentación";

      Swal.fire({
        title: "¡Error!",
        icon: "error",
        text: backendMessage,
        confirmButtonColor: "#1ab394",
        confirmButtonText: "Continuar",
      });
    }
  };

  // Maneja la selección de tipo de documento y abre input correspondiente
  const handleSelection = (event, value) => {
    setSelectedDocTypeOption(value);

    setTimeout(() => {
      if (value?.value === 2 && fileInputRef.current) {
        fileInputRef.current.click();
      } else if (value?.value === 1 && folderInputRef.current) {
        folderInputRef.current.click();
      }
    }, 100);
  };

  // Actualiza lista de archivos seleccionados
  const handleFileChange = (event) => {
    const selected = Array.from(event.target.files || []);
    setFiles(selected);
    if (event.target) {
      event.target.value = "";
    }
  };

  useEffect(() => {
    if (selectedFolderTypeOption) {
      setOpenUploader(true);
    }
  }, [selectedFolderTypeOption]);

  return (
    <Dialog
      open={openUploader}
      onClose={handleCloseModal}
      TransitionComponent={effects}
      fullWidth
      maxWidth="sm"
      aria-labelledby="modal-titulo"
      aria-describedby="modal-descripcion"
    >
      {/* Título del diálogo */}
      <DialogTitle id="modal-titulo" sx={{ m: 0, p: 2 }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <CloudUpload />
          <span style={{ color: "#002451" }}>SUBIR DOCUMENTACIÓN</span>
          <IconButton
            color="error"
            aria-label="cerrar"
            onClick={handleCloseModal}
          >
            <Close />
          </IconButton>
        </Grid>
      </DialogTitle>

      {/* Contenido del diálogo */}
      <DialogContent dividers id="modal-descripcion">
        {/* Inputs ocultos */}
        <input
          type="file"
          accept=".pdf,.docx,image/*"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
          multiple
        />
        <input
          type="file"
          accept="*"
          ref={folderInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
          multiple
        />

        {/* Campo según tipo de carpeta */}
        {folderTypeFields[selectedFolderTypeOption?.value] || null}

        {/* Checkbox para guardar con nombre de archivo */}
        <Grid
          item
          xs={12}
          sx={{
            padding: "0px 10px 10px 10px",
            border: "1px dashed #f4f9ff",
            padding: "10px",
            backgroundColor: "#f4f9ff",
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={uploadDocWithFileName}
                onChange={(e) => setUploadDocWithFileName(e.target.checked)}
              />
            }
            label={
              <Typography
                style={{
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                }}
                variant="contained"
              >
                Guardar con el nombre por defecto del archivo.
              </Typography>
            }
          />
        </Grid>

        {/* Lista de archivos seleccionados */}
        {files.length > 0 && (
          <div style={{ fontSize: "14px" }}>
            <hr />
            <span>Archivos seleccionados:</span>
            <hr />
            <ol style={{ padding: "20px", margin: 0 }}>
              {files.map((file, i) => (
                <li
                  key={i}
                  style={{
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                    whiteSpace: "normal",
                    marginBottom: "3px",
                  }}
                >
                  {file.name}
                </li>
              ))}
            </ol>
          </div>
        )}
      </DialogContent>

      {/* Acciones del diálogo */}
      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          onClick={handleUpload}
          sx={{ minHeight: "36px" }}
          startIcon={<FileUpload />}
        >
          Subir
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploaderDialog;
